import { postRecommendEnLabel } from "@/apis/ai/route";
import { SimilarTagType } from "@/apis/taxonomy/types";
import Input from "@/components/common/input";
import { tagExtendFormSchema } from "@/components/mapping/extend/schemas";
import { Button } from "@/styles/ui/button";
import { FormField } from "@/styles/ui/form";
import { Dimension } from "@/types/mapping";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Pencil } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";

export type TagExtendFormData = z.infer<typeof tagExtendFormSchema>;

export function ExtensionInputKorLabel({
  form,
  koLabel,
  tagExists,
  conflictTag,
  selectedExistingTag,
  setKoLabel,
  setSelectedExistingTag,
  fieldName = "koLabel",
}: {
  form: UseFormReturn<any>;
  koLabel: string;
  tagExists: boolean;
  conflictTag: SimilarTagType | null;
  setKoLabel: (koLabel: string) => void;
  selectedExistingTag: SimilarTagType | null;
  setSelectedExistingTag: (selectedExistingTag: SimilarTagType | null) => void;
  fieldName?: string;
}) {
  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="label-1-normal-medium">한글 라벨</div>
        <div className="flex items-center gap-2 self-stretch w-full relative">
          <FormField
            control={form.control}
            name={fieldName}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1 w-full relative">
                <Input
                  value={koLabel}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setKoLabel(e.target.value);
                    setSelectedExistingTag(null);
                    form.setValue("enLabel", "");
                  }}
                  placeholder="확장 태그의 한글 라벨을 입력하세요."
                  size="large"
                  className="rounded-[8px]"
                  isError={
                    !!fieldState.error ||
                    (tagExists && selectedExistingTag == null)
                  }
                />
                {fieldState.error && (
                  <div className="caption-2-regular text-red-500 ml-0.5">
                    {fieldState.error.message}
                  </div>
                )}
                {!fieldState.error &&
                  tagExists &&
                  selectedExistingTag == null && (
                    <div className="flex items-center gap-1 ml-0.5">
                      <span className="caption-2-regular text-red-500">
                        동일한 한글 라벨의 태그가 존재합니다.
                      </span>
                      {conflictTag && (
                        <button
                          type="button"
                          className="caption-2-regular text-blue-500 underline underline-offset-2 shrink-0"
                          onClick={() => {
                            setSelectedExistingTag(conflictTag);
                            setKoLabel(conflictTag.koLabel);
                            form.setValue("koLabel", conflictTag.koLabel);
                            form.setValue("enLabel", conflictTag.enLabel);
                            form.clearErrors("enLabel");
                          }}
                        >
                          기존 태그 사용
                        </button>
                      )}
                    </div>
                  )}
              </div>
            )}
          />
        </div>
      </div>
    </>
  );
}

export function ExtensionInputEnLabel({
  workbookId,
  roleKey,
  form,
  selectedExistingTag,
  tagExists,
  fieldName = "enLabel",
  setSelectedExistingTag,
  dimension,
}: {
  workbookId: string;
  roleKey: string;
  tagExists: boolean;
  dimension: Dimension;
  form: UseFormReturn<any>;
  selectedExistingTag: SimilarTagType | null;
  fieldName?: string;
  setSelectedExistingTag: (selectedExistingTag: SimilarTagType | null) => void;
}) {
  const { mutate, isPending } = useMutation({
    mutationFn: (koreanLabel: string) => {
      const parentTagKey = form.getValues("parentTagKey");
      if (parentTagKey === "") {
        form.setError("parentTagKey", {
          message: "축을 선택하세요.",
        });
      }
      return postRecommendEnLabel(
        workbookId,
        parentTagKey,
        roleKey,
        koreanLabel,
        dimension,
      );
    },
    onSuccess: (data, variables) => {
      form.setValue(fieldName, data.enLabel);
      form.trigger(fieldName);
    },
  });

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="label-1-normal-medium">영문 라벨</div>
        <div className="flex items-start gap-2 self-stretch w-full">
          <FormField
            control={form.control}
            name={fieldName}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1 w-full relative">
                <Input
                  placeholder="확장 태그의 영문 라벨을 입력하세요."
                  size="large"
                  className="rounded-[8px]"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setSelectedExistingTag(null);
                  }}
                  isError={
                    !!fieldState.error ||
                    (tagExists && selectedExistingTag == null)
                  }
                  disabled={selectedExistingTag != null}
                />
                {fieldState.error && (
                  <div className="caption-2-regular text-red-500 ml-0.5">
                    {fieldState.error.message}
                  </div>
                )}
                {!fieldState.error &&
                  tagExists &&
                  selectedExistingTag == null && (
                    <div className="caption-2-regular text-red-500 ml-0.5">
                      동일한 영문 라벨의 태그가 존재합니다.
                    </div>
                  )}
              </div>
            )}
          />
          <Button
            type="button"
            variant="ghost"
            className="flex h-9 py-2 px-4 justify-center items-center gap-2 rounded-[8px] bg-[#FFFFFF] shadow-sm border-[1px] border-[#E5E5E5]"
            onClick={(e) => {
              e.preventDefault();
              mutate(form.getValues("koLabel"));
            }}
            disabled={isPending || selectedExistingTag != null}
          >
            {isPending ? (
              <Loader2 className="w-[16px] h-[16px] flex-shrink-0 aspect-square animate-spin" />
            ) : (
              <Pencil className="w-[16px] h-[16px] flex-shrink-0 aspect-square" />
            )}
            <div className="label-1-normal-medium">자동 생성</div>
          </Button>
        </div>
      </div>
    </>
  );
}
